import { CircularProgress, Stack, styled, Theme, Typography, useTheme } from "@mui/material";
import { addDays, format, isThisMonth } from "date-fns";
import { HomepageSparklineDay } from "../../../graphql/types";

import { JSX } from "react/jsx-runtime";
import AccentDanger from "../../../icons/AccentDanger.svg?react";
import AccentInfo from "../../../icons/AccentInfo.svg?react";
import AccentSuccess from "../../../icons/AccentSuccess.svg?react";
import Issues from "../../../icons/Issues.svg?react";
import { getSparklineColor, SparklineStatus, TODAY_STRING } from "./utils";

import { IfPermission } from "../../../components";
import { UnsuspendTransferButton } from "../../../components/UnsuspendTransfer/UnsuspendTransferButton";
import Permissions from "../../../constants/permissions";

const AccordionColumns = styled("div", {
  shouldForwardProp: (prop) => prop !== "isDark",
})<{
  isDark?: boolean;
}>(({ isDark, theme }) => ({
  display: "grid",
  gridTemplateColumns: "60px 1fr 250px 80px",
  backgroundColor: isDark ? theme.palette.surfaceLight20.main : theme.palette.surfaceLight10.main,
  "&:last-child": {
    borderRadius: "0 0 8px 8px",
  },
}));

const getBorder = (status: string, theme: Theme, isSelected?: boolean) => {
  if (isSelected) {
    return `2px solid ${theme.palette.text50.main}`;
  }
  if (status === SparklineStatus.Expected) {
    return `1px solid ${theme.palette.text20.main}`;
  }
  return undefined;
};

const getCalendarBorderColor = (status: string, theme: Theme): string => {
  switch (status) {
    case SparklineStatus.Clean:
      return theme.palette.accentSuccess50.main;
    case SparklineStatus.Warning:
      return theme.palette.accentInfo50.main;
    case SparklineStatus.Issue:
      return theme.palette.accentWarning50.main;
    case SparklineStatus.Failed:
      return theme.palette.accentDanger50.main;
    case SparklineStatus.Missing:
    case SparklineStatus.Expected:
      return theme.palette.text50.main;
    default:
      return theme.palette.surfaceLight10.main;
  }
};

const SparklineSquare = ({
  sparkline,
  day,
  isBig,
  isSelected,
  onClick,
}: {
  sparkline: HomepageSparklineDay;
  day: string;
  isBig?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
}) => {
  const shouldBeCentered =
    sparkline.status === SparklineStatus.Missing ||
    (sparkline.status === SparklineStatus.Expected && !sparkline.running);
  const justifyContent: "flex-start" | "center" = shouldBeCentered ? "center" : "flex-start";
  const theme = useTheme();
  return (
    <Stack
      data-testid={`sparkline-square-${day}`}
      onClick={onClick}
      sx={(theme) => ({
        height: isBig ? "36px" : "32px",
        width: isBig ? "36px" : "22px",
        borderRadius: "4px",
        alignItems: "center",
        justifyContent,
        background: getSparklineColor(sparkline.status, theme),
        border: isSelected
          ? `2px solid ${getCalendarBorderColor(sparkline.status, theme)}`
          : getBorder(sparkline.status, theme, isSelected),
        boxSizing: "border-box",
        "&:hover": {
          cursor: isBig ? "pointer" : "default",
        },
      })}
    >
      <Stack
        sx={{ alignItems: "center", justifyContent: "center", height: "100%", width: "100%" }}
        data-testid={`sparkline-status-${sparkline.status}`}
      >
        {sparkline.status !== SparklineStatus.Missing ? (
          <Stack sx={{ alignItems: "center" }}>
            <Typography variant="body.xSmall.regular" sx={{ color: "text50.main" }}>
              {day}
            </Typography>
            {sparkline.running ? (
              <CircularProgress
                size="6px"
                sx={(theme) => ({
                  color:
                    sparkline.status === SparklineStatus.Failed ? theme.palette.text10.main : theme.palette.text50.main,
                })}
                data-testid="sparkline-running"
              />
            ) : (
              sparkline.status !== SparklineStatus.Expected && (
                <Stack
                  sx={{ mt: "-3px", width: "18px", height: "18px", alignItems: "center" }}
                  data-testid={`status-${sparkline.status}`}
                >
                  {getSparklineIcon(sparkline.status, theme)}
                </Stack>
              )
            )}
          </Stack>
        ) : (
          <Typography variant="body.xSmall.regular" sx={{ color: "text50.main" }}>
            {day}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

const SevenDaysSparkline = ({
  sparklines,
  sparklineFirstDate,
}: {
  sparklines: HomepageSparklineDay[];
  sparklineFirstDate: string;
}) => {
  if (!sparklineFirstDate) {
    console.error("sparklineFirstDate is empty");
    return null;
  }
  const [year, month, day] = sparklineFirstDate.split("-");
  const sparklineFirstDateAux = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 0, 0, 0, 0);
  if (isThisMonth(sparklineFirstDateAux)) {
    return (
      <Stack direction="row" sx={{ width: "100%", gap: "4px", alignItems: "center", justifyContent: "flex-end" }}>
        <Typography variant="body.xSmall.regular" sx={{ color: "text30.main" }}>
          {format(sparklineFirstDateAux, "LLL")}
        </Typography>
        {sparklines.map((sparkline, index) => (
          <SparklineSquare key={index} sparkline={sparkline} day={format(addDays(sparklineFirstDateAux, index), "d")} />
        ))}
      </Stack>
    );
  } else {
    const previousMonthDays = [sparklineFirstDateAux];
    const currentMonthDays: Date[] = [];
    for (let i = 1; i < 7; i++) {
      if (isThisMonth(addDays(sparklineFirstDateAux, i))) {
        currentMonthDays.push(addDays(sparklineFirstDateAux, i));
      } else {
        previousMonthDays.push(addDays(sparklineFirstDateAux, i));
      }
    }
    const previousMonthSparkline = sparklines.slice(0, previousMonthDays.length);
    const currentMonthSparkline = sparklines.slice(previousMonthDays.length, sparklines.length);
    return (
      <Stack direction="row" sx={{ width: "100%", gap: "8px", alignItems: "center", justifyContent: "flex-end" }}>
        <Stack direction="row" sx={{ gap: "4px", alignItems: "center" }}>
          <Typography variant="body.xSmall.regular" sx={{ color: "text30.main" }}>
            {format(sparklineFirstDateAux, "LLL")}
          </Typography>
          {previousMonthSparkline.map((sparkline, index) => (
            <SparklineSquare
              sparkline={sparkline}
              key={index}
              day={format(addDays(sparklineFirstDateAux, index), "d")}
            />
          ))}
        </Stack>
        <div style={{ width: "1px", height: "28px", borderRight: "1px dashed" }} />
        <Stack direction="row" sx={{ gap: "4px", alignItems: "center" }}>
          <Typography variant="body.xSmall.regular" sx={{ color: "text30.main" }}>
            {format(TODAY_STRING, "LLL")}
          </Typography>
          {currentMonthSparkline.map((sparkline, index) => (
            <SparklineSquare sparkline={sparkline} key={index} day={format(currentMonthDays[index], "d")} />
          ))}
        </Stack>
      </Stack>
    );
  }
};

const resolveIcon = (status: string, theme: Theme): JSX.Element => {
  const iconProps = {
    width: "18px",
    height: "18px",
    color: theme.palette.text10.main,
  };

  switch (status) {
    case "error":
      return <AccentDanger {...iconProps} fill={theme.palette.accentDanger30.main} data-testid="danger-icon" />;
    case "warn":
      return <AccentInfo {...iconProps} fill={theme.palette.accentInfo30.main} data-testid="info-icon" />;
    case "dirtyWarn":
    case "warn2":
      return <Issues {...iconProps} fill={theme.palette.accentWarning30.main} data-testid="issue-icon" />;
    default:
      return (
        <AccentSuccess
          {...iconProps}
          fill={theme.palette.accentSuccess30.main}
          stroke={theme.palette.text10.main}
          data-testid="success-icon"
        />
      );
  }
};

type NameAndEventProps = {
  name: string;
  status: {
    msg: string;
    status: string;
  };
  artifactStatus: string;
  destination?: string;
  type: "download" | "upload";
  refreshData: () => Promise<void>;
  refreshing: boolean;
};

const NameAndEvent = ({
  name,
  status,
  destination,
  type,
  artifactStatus,
  refreshData,
  refreshing,
}: NameAndEventProps) => {
  const theme = useTheme();
  const icon = resolveIcon(status.status, theme);

  return (
    <>
      <Typography sx={{ padding: "12px 12px 4px 12px", color: "text50.main" }} variant="body.medium.semibold">
        {name}
      </Typography>
      {destination && (
        <Stack sx={{ padding: "8px 12px 8px 24px" }}>
          <Typography sx={{ color: "text40.main" }} variant="body.xSmall.regular">
            {destination}
          </Typography>
        </Stack>
      )}
      {status.status !== "success" && (
        <Stack direction="row" sx={{ gap: "4px", padding: "8px 12px 8px 24px", display: "flex", alignItems: "center" }}>
          <Stack sx={{ alignItems: "center" }} data-testid="event-icon">
            {icon}
          </Stack>
          <Typography
            variant="body.small.regular"
            sx={{ mt: "2px", minHeight: "24px", color: "text50.main" }}
            data-testid="status-msg"
          >
            {status.msg}
          </Typography>
          <div className="actionsGroup">
            {artifactStatus === "suspended" && (
              <IfPermission permission={Permissions.DevelopScripts}>
                <UnsuspendTransferButton
                  name={name}
                  type={type}
                  refreshData={refreshData}
                  refreshing={refreshing}
                  inline
                />
              </IfPermission>
            )}
          </div>
        </Stack>
      )}
    </>
  );
};

const getSparklineIcon = (status: string, theme: Theme): JSX.Element => {
  const iconProps = {
    width: "17px",
    height: "17px",
    color: theme.palette.text10.main,
  };
  switch (status) {
    case SparklineStatus.Clean:
      return <AccentSuccess {...iconProps} fill={theme.palette.accentSuccess30.main} data-testid="success-icon" />;
    case SparklineStatus.Warning:
      return <AccentInfo {...iconProps} fill={theme.palette.accentInfo30.main} data-testid="info-icon" />;
    case SparklineStatus.Issue:
      return <Issues {...iconProps} fill={theme.palette.accentWarning30.main} data-testid="issue-icon" />;
    case SparklineStatus.Failed:
      return <AccentDanger {...iconProps} fill={theme.palette.accentDanger30.main} data-testid="danger-icon" />;
    default:
      return <>-</>;
  }
};

const NoItemsOrLoadingContainer = styled(Stack)(({ theme }) => ({
  height: "200px",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "8px",
  backgroundColor: theme.palette.surfaceLight10.main,
  border: `1px solid ${theme.palette.surfaceLight30.main}`,
  gap: "10px",
})) as typeof Stack;

const NoItems = ({ message = "No results, try another filter" }: { message?: string }) => {
  const theme = useTheme();
  return (
    <NoItemsOrLoadingContainer direction="row" sx={{ padding: "0px 40px" }}>
      <AccentInfo width="24px" height="24px" fill={theme.palette.accentInfo30.main} color={theme.palette.text10.main} />
      <Typography variant="body.large.regular" sx={{ color: "text40.main" }}>
        {message}
      </Typography>
    </NoItemsOrLoadingContainer>
  );
};

export { AccordionColumns, NameAndEvent, NoItems, NoItemsOrLoadingContainer, SevenDaysSparkline, SparklineSquare };
